import { useApi, ApiPromise } from '@/api/useApi.ts';
import { IActiveTabPayload } from '@/interfaces/powerMarkets/payloads/IActiveTabPayload.ts';
import { IDashboardShareStatus } from '@/interfaces/powerMarkets/dashboard/IDashboardShareStatus.ts';
import { IDeleteItemWithOrderingPayload } from '@/interfaces/powerMarkets/payloads/IDeleteItemWithOrderingPayload.ts';
import { IItemsWithOrderingPayload } from '@/interfaces/powerMarkets/payloads/IItemsWithOrderingPayload.ts';
import { IPageData } from '@/interfaces/general/IPageData.ts';
import { IPageSettingsPayload } from '@/interfaces/powerMarkets/payloads/IPageSettingsPayload.ts';
import { IShareDashboardPayload, IShareDashboardToUserPayload } from '@/interfaces/powerMarkets/payloads/IShareDashboardPayload.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { ISubscriptionSettings } from '@/interfaces/powerMarkets/notifications/ISubscriptionSettings.ts';
import { IUserSettings } from '@/interfaces/powerMarkets/settings/IUserSettings.ts';
import { IUserTableFiltersWithName } from '@/interfaces/powerMarkets/payloads/IUserTableFiltersWithName.ts';
import { IDashboard } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { ICreateUserPayload } from '@/interfaces/powerMarkets/payloads/ICreatUserPayload.ts';

const userSettingsApiCall = useApi('userSettings');
const apiStaticCall = useApi('static');

function deleteCompanyDashboard (id: string): ApiPromise<void> {
    return userSettingsApiCall.get<void>({
        endpoint: `/saveCompanyDashboards/delete/${id}`,
    });
}

function deleteItemWithOrdering (payload: IDeleteItemWithOrderingPayload): ApiPromise<void> {
    return userSettingsApiCall.post<IDeleteItemWithOrderingPayload, void>({
        endpoint: '/itemsWithOrdering/delete',
        payload,
    });
}

function getCompanyDashboards (): ApiPromise<Array<IPageData<IDashboard>>> {
    return userSettingsApiCall.get<Array<IPageData<IDashboard>>>({
        endpoint: '/saveCompanyDashboards',
    });
}

function getCompanyUsers (): ApiPromise<Array<string>> {
    return userSettingsApiCall.get<Array<string>>({
        endpoint: '/getCompanyUsers',
    });
}

async function getUserSettings (): Promise<IUserSettings> {
    return (await userSettingsApiCall.get<IUserSettings>({
        endpoint: '/getUserSettings',
    })).data;
}

// to be removed and changed to beacon
function setUserOnline (): ApiPromise<void> {
    return apiStaticCall.get<void>({
        endpoint: '/userOnline',
    });
}

function postShareDashboardsWithCompany (payload: IShareDashboardPayload): ApiPromise<void> {
    return userSettingsApiCall.post<IShareDashboardPayload, void>({
        endpoint: '/saveCompanyDashboards',
        payload,
    });
}

function postShareDashboardsWithUser (payload: IShareDashboardToUserPayload): ApiPromise<IDashboardShareStatus> {
    return userSettingsApiCall.post<IShareDashboardToUserPayload, IDashboardShareStatus>({
        endpoint: '/sendDashboards',
        payload,
    });
}

function postUserTableFilters (payload: IUserTableFiltersWithName): ApiPromise<void> {
    return userSettingsApiCall.post<IUserTableFiltersWithName, void>({
        endpoint: '/postgenerictablefilters',
        payload,
    });
}

function postItemWithOrdering<T> (payload: IItemsWithOrderingPayload<T>): ApiPromise<void> {
    return userSettingsApiCall.post<IItemsWithOrderingPayload<T>, void>({
        endpoint: '/itemsWithOrdering/update',
        payload,
    });
}

function postNotificationSetting (payload: IItemsWithOrderingPayload<ISubscriptionSettings>): ApiPromise<void> {
    return userSettingsApiCall.post<IItemsWithOrderingPayload<ISubscriptionSettings>, void>({
        endpoint: '/itemsWithOrdering/updateNotification',
        payload,
    });
}

async function postPageSettings<T> (payload: IPageSettingsPayload<T>): ApiPromise<void> {
    return userSettingsApiCall.post<IPageSettingsPayload<T>, void>({
        endpoint: '/postpagesettings',
        payload,
    });
}

function postUserSettings (payload: ISiteSettings): ApiPromise<void> {
    return userSettingsApiCall.post<ISiteSettings, void>({
        endpoint: '/postusersettings',
        payload,
    });
}

function postCreateNewUser (settings: ISiteSettings): ApiPromise<void> {
    const payload: ICreateUserPayload = {
        id: settings.userId,
        settings,
    };
    return userSettingsApiCall.post<ICreateUserPayload, void>({
        endpoint: '/postusersettings/newUser',
        payload,
    });
}

function postActiveTab (payload: IActiveTabPayload): ApiPromise<null> {
    return userSettingsApiCall.post<IActiveTabPayload | '', null>({
        endpoint: '/itemsWithOrdering/activeTab',
        payload,
    });
}

export const userSettingsApi = {
    deleteCompanyDashboard,
    deleteItemWithOrdering,
    getCompanyDashboards,
    getCompanyUsers,
    getUserSettings,
    postActiveTab,
    postUserTableFilters,
    postItemWithOrdering,
    postNotificationSetting,
    postPageSettings,
    postShareDashboardsWithCompany,
    postShareDashboardsWithUser,
    postUserSettings,
    postCreateNewUser,
    setUserOnline,
};
